import { autoinject, PLATFORM } from 'aurelia-framework';
import { RouterConfiguration, Router } from 'aurelia-router';
import { LocationContext } from 'app/shared/lib/location-context';
import { IEssetteApplicationVersion, version } from 'version';
import { LogoutContext } from 'app/shared/lib/logout-context';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AppService, IApiVersionMetadata } from 'app/shared/services/app-service';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { I18N } from 'aurelia-i18n';

@autoinject()
export class LoginViewModel {

    protected version: IEssetteApplicationVersion = version;
    protected router!: Router;
    protected warning: string = '';
    protected showLogoutContextMessage: boolean = false;

    constructor(
        protected env: EnvironmentService,
        private readonly logger: AppLogger,
        protected logoutContext: LogoutContext,
        private readonly i18n: I18N,
        private location: LocationContext,
        private appService: AppService) {

    }

    protected activate(): void {
        this.warning = '';
        this.loadMetadata();
        this.showLogoutContextMessage = this.logoutContext.message == this.i18n.tr('ui:messages.sessionExpired');
    }

    protected configureRouter(config: RouterConfiguration, router: Router) {
        config.map([
            { route: '', redirect: 'login' },
            { route: 'login', name: 'checkin', moduleId: PLATFORM.moduleName('app/core/pages/checkin'), title: 'core.login' },
            { route: 'recover/:subject', name: 'forgot', moduleId: PLATFORM.moduleName('app/core/pages/recover'), title: 'core.recover' },
            { route: 'verify', name: 'verify', moduleId: PLATFORM.moduleName('app/core/pages/verify'), title: 'core.verify' },
            { route: 'reset', name: 'change-password', moduleId: PLATFORM.moduleName('app/core/pages/change-password'), title: 'core.reset' },
            { route: 'accept', name: 'accept-invitation', moduleId: PLATFORM.moduleName('app/core/pages/accept-invitation'), title: 'core.accept' },
            { route: 'sso', name: 'sso', moduleId: PLATFORM.moduleName('app/core/pages/sso'), title: 'core.sso' },
            { route: 'signon', name: 'signon', moduleId: PLATFORM.moduleName('app/core/pages/signon/signon'), title: 'core.signon' },
            { route: 'signout', name: 'signout', moduleId: PLATFORM.moduleName('app/core/pages/signon/signout'), title: 'core.signout' },
            {
                route: 'unknown', redirect: 'login', navigationStrategy: (instruction) => {
                    if(router.isNavigatingFirst && !this.location.path) {
                        this.location.path = location.hash;
                    }
                }
            }
        ]);
        config.mapUnknownRoutes({ redirect: 'unknown' });

        this.router = router;
    }

    private checkVersions(name1: string, version1: string, patch1: number, name2: string, version2: string, patch2: number): void {
        const ignore = this.env.config.essetteApi.ignoreVersionCheck;
        if(version1 !== version2 || patch1 !== patch2) {
            const message = `${name1} Version 'v${version1}.${patch1}' does not match ${name2} Version 'v${version2}.${patch2}'.`;
            this.logger.log({ message, style: ignore ? '' : 'font-size:1.5rem;', level: version1 === version2 ? 'warn' : 'error' });
            if(!ignore && !this.warning) {
                this.warning = message;
            }
        }
    }

    private processMetadata(metadata: IApiVersionMetadata): void {
        const appVersion = version?.version || '';
        const appPatch = version?.patch || 0;
        const apiVersion = metadata?.api?.version || '';
        const apiPatch = metadata?.api?.patch || 0;
        const dbVersion = metadata?.database?.version;
        const dbPatch = metadata?.database?.patch || 0;

        const details = {
            version,
            api: metadata?.api,
            database: metadata?.database
        };

        this.logger.info(
            `Version: App v${version?.description}, API v${metadata?.api?.description}, DB v${metadata?.database?.description}`,
            details);

        if(appVersion && apiVersion) {
            this.checkVersions('App', appVersion, appPatch, 'API', apiVersion, apiPatch);
        }
        if(apiVersion && dbVersion) {
            this.checkVersions('API', apiVersion, apiPatch, 'DB', dbVersion, dbPatch);
        }
    }

    private async loadMetadata(): Promise<void> {
        try {
            this.processMetadata(await this.appService.getVersionMetadata());
        }
        catch {
            this.warning = 'There was a problem connecting to the Essette system.';
        }
    }

}
